<template>
  <div class="stage-bracket">
    <div
      v-if="bracketBlocks.length"
      :class="[
        'stage-bracket__stack',
        { 'stage-bracket__stack--split': bracketBlocks.length > 1 }
      ]"
    >
      <section
        v-for="block in bracketBlocks"
        :key="block.key"
        :class="['stage-bracket__layout', `stage-bracket__layout--${block.layout}`]"
      >
        <template v-if="block.layout === 'single'">
          <div class="stage-bracket__center">
            <div class="stage-bracket__column stage-bracket__column--center">
              <header
                v-if="block.label"
                class="stage-bracket__column-header stage-bracket__column-header--center"
              >
                <span>{{ block.label }}</span>
              </header>

              <div class="stage-bracket__matches stage-bracket__matches--center">
                <article
                  v-for="match in block.allMatches"
                  :key="match.key"
                  class="stage-bracket__match"
                >
                  <div class="stage-bracket__slots">
                    <button
                      v-for="participant in match.participants"
                      :key="participant.key"
                      type="button"
                      class="stage-bracket__slot"
                      :class="slotClasses(participant)"
                      :title="participantTitle(participant)"
                      :aria-label="participantTitle(participant)"
                      :disabled="!isClickable(participant.sourceRound)"
                      @click="handleAthleteClick(participant)"
                    >
                      <span class="stage-bracket__avatar-wrap">
                        <img
                          :src="participant.image || defaultAvatar"
                          class="stage-bracket__avatar"
                          alt=""
                        />

                        <span
                          v-if="participant.isWinner && !resolveSpecialStatusKey(participant)"
                          class="stage-bracket__winner-mark"
                          aria-hidden="true"
                        >
                          <i class="fa-solid fa-crown"></i>
                        </span>

                        <span
                          v-if="resolveSpecialStatusIcon(participant)"
                          :class="[
                            'stage-bracket__slot-state',
                            'stage-bracket__slot-state--overlay',
                            `stage-bracket__slot-state--${resolveSpecialStatusKey(participant)}`
                          ]"
                          aria-hidden="true"
                        >
                          <i :class="resolveSpecialStatusIcon(participant)"></i>
                        </span>
                      </span>

                      <span class="stage-bracket__slot-copy">
                        <strong class="stage-bracket__slot-name">{{ participant.name }}</strong>

                        <span v-if="needsAlert(participant.sourceRound)" class="stage-bracket__alert-pill">
                          <span class="stage-bracket__alert-pill-track">
                            <span class="stage-bracket__alert-pill-text">{{ $t('judge.message-alert') }}</span>
                          </span>
                        </span>

                        <span
                          v-if="isOwner(participant.sourceRound)"
                          class="stage-bracket__check-pill"
                          :class="{ 'stage-bracket__check-pill--valid': isValid(participant.sourceRound) }"
                        >
                          <span class="check-wrapper">
                            <i class="fas fa-check"></i>
                            <i class="fas fa-check"></i>
                          </span>
                        </span>

                        <span
                          v-if="slotStatusKey(participant.sourceRound)"
                          :class="[
                            'stage-bracket__status-pill',
                            `stage-bracket__status-pill--${slotStatusKey(participant.sourceRound)}`
                          ]"
                        >
                          {{ statusLabel(participant.sourceRound) }}
                        </span>
                      </span>

                      <strong class="stage-bracket__slot-score">{{ participantScore(participant) }}</strong>
                    </button>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <section class="stage-bracket__side stage-bracket__side--left">
            <div class="stage-bracket__column">
              <header v-if="block.label" class="stage-bracket__column-header">
                <span>{{ block.label }}</span>
              </header>

              <div class="stage-bracket__match-groups">
                <div
                  v-for="group in buildConnectorGroups(block.leftMatches, 'left')"
                  :key="group.key"
                  class="stage-bracket__match-group"
                >
                  <div class="stage-bracket__match-group-body">
                    <article
                      v-for="match in group.matches"
                      :key="match.key"
                      class="stage-bracket__match"
                    >
                      <div class="stage-bracket__slots">
                        <button
                          v-for="participant in match.participants"
                          :key="participant.key"
                          type="button"
                          class="stage-bracket__slot"
                          :class="slotClasses(participant)"
                          :title="participantTitle(participant)"
                          :aria-label="participantTitle(participant)"
                          :disabled="!isClickable(participant.sourceRound)"
                          @click="handleAthleteClick(participant)"
                        >
                          <span class="stage-bracket__avatar-wrap">
                            <img
                              :src="participant.image || defaultAvatar"
                              class="stage-bracket__avatar"
                              alt=""
                            />

                            <span
                              v-if="participant.isWinner && !resolveSpecialStatusKey(participant)"
                              class="stage-bracket__winner-mark"
                              aria-hidden="true"
                            >
                              <i class="fa-solid fa-crown"></i>
                            </span>

                            <span
                              v-if="resolveSpecialStatusIcon(participant)"
                              :class="[
                                'stage-bracket__slot-state',
                                'stage-bracket__slot-state--overlay',
                                `stage-bracket__slot-state--${resolveSpecialStatusKey(participant)}`
                              ]"
                              aria-hidden="true"
                            >
                              <i :class="resolveSpecialStatusIcon(participant)"></i>
                            </span>
                          </span>

                          <span class="stage-bracket__slot-copy">
                            <strong class="stage-bracket__slot-name">{{ participant.name }}</strong>

                            <span v-if="needsAlert(participant.sourceRound)" class="stage-bracket__alert-pill">
                              <span class="stage-bracket__alert-pill-track">
                                <span class="stage-bracket__alert-pill-text">{{ $t('judge.message-alert') }}</span>
                              </span>
                            </span>

                            <span
                              v-if="isOwner(participant.sourceRound)"
                              class="stage-bracket__check-pill"
                              :class="{ 'stage-bracket__check-pill--valid': isValid(participant.sourceRound) }"
                            >
                              <span class="check-wrapper">
                                <i class="fas fa-check"></i>
                                <i class="fas fa-check"></i>
                              </span>
                            </span>

                            <span
                              v-if="slotStatusKey(participant.sourceRound)"
                              :class="[
                                'stage-bracket__status-pill',
                                `stage-bracket__status-pill--${slotStatusKey(participant.sourceRound)}`
                              ]"
                            >
                              {{ statusLabel(participant.sourceRound) }}
                            </span>
                          </span>

                          <strong class="stage-bracket__slot-score">{{ participantScore(participant) }}</strong>
                        </button>
                      </div>
                    </article>
                  </div>

                  <div class="stage-bracket__connector stage-bracket__connector--left" aria-hidden="true">
                    <div
                      :class="[
                        'stage-bracket__connector-segment',
                        group.matches.length > 1
                          ? 'stage-bracket__connector-segment--branch'
                          : 'stage-bracket__connector-segment--single'
                      ]"
                    >
                      <span class="stage-bracket__connector-line stage-bracket__connector-line--top"></span>
                      <span class="stage-bracket__connector-line stage-bracket__connector-line--bottom"></span>
                      <span class="stage-bracket__connector-spine"></span>
                      <span class="stage-bracket__connector-line stage-bracket__connector-line--out"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section class="stage-bracket__side stage-bracket__side--right">
            <div class="stage-bracket__column">
              <header v-if="block.label" class="stage-bracket__column-header">
                <span>{{ block.label }}</span>
              </header>

              <div class="stage-bracket__match-groups">
                <div
                  v-for="group in buildConnectorGroups(block.rightMatches, 'right')"
                  :key="group.key"
                  class="stage-bracket__match-group stage-bracket__match-group--right"
                >
                  <div class="stage-bracket__connector stage-bracket__connector--right" aria-hidden="true">
                    <div
                      :class="[
                        'stage-bracket__connector-segment',
                        group.matches.length > 1
                          ? 'stage-bracket__connector-segment--branch'
                          : 'stage-bracket__connector-segment--single'
                      ]"
                    >
                      <span class="stage-bracket__connector-line stage-bracket__connector-line--top"></span>
                      <span class="stage-bracket__connector-line stage-bracket__connector-line--bottom"></span>
                      <span class="stage-bracket__connector-spine"></span>
                      <span class="stage-bracket__connector-line stage-bracket__connector-line--out"></span>
                    </div>
                  </div>

                  <div class="stage-bracket__match-group-body">
                    <article
                      v-for="match in group.matches"
                      :key="match.key"
                      class="stage-bracket__match"
                    >
                      <div class="stage-bracket__slots">
                        <button
                          v-for="participant in match.participants"
                          :key="participant.key"
                          type="button"
                          class="stage-bracket__slot"
                          :class="slotClasses(participant)"
                          :title="participantTitle(participant)"
                          :aria-label="participantTitle(participant)"
                          :disabled="!isClickable(participant.sourceRound)"
                          @click="handleAthleteClick(participant)"
                        >
                          <span class="stage-bracket__avatar-wrap">
                            <img
                              :src="participant.image || defaultAvatar"
                              class="stage-bracket__avatar"
                              alt=""
                            />

                            <span
                              v-if="participant.isWinner && !resolveSpecialStatusKey(participant)"
                              class="stage-bracket__winner-mark"
                              aria-hidden="true"
                            >
                              <i class="fa-solid fa-crown"></i>
                            </span>

                            <span
                              v-if="resolveSpecialStatusIcon(participant)"
                              :class="[
                                'stage-bracket__slot-state',
                                'stage-bracket__slot-state--overlay',
                                `stage-bracket__slot-state--${resolveSpecialStatusKey(participant)}`
                              ]"
                              aria-hidden="true"
                            >
                              <i :class="resolveSpecialStatusIcon(participant)"></i>
                            </span>
                          </span>

                          <span class="stage-bracket__slot-copy">
                            <strong class="stage-bracket__slot-name">{{ participant.name }}</strong>

                            <span v-if="needsAlert(participant.sourceRound)" class="stage-bracket__alert-pill">
                              <span class="stage-bracket__alert-pill-track">
                                <span class="stage-bracket__alert-pill-text">{{ $t('judge.message-alert') }}</span>
                              </span>
                            </span>

                            <span
                              v-if="isOwner(participant.sourceRound)"
                              class="stage-bracket__check-pill"
                              :class="{ 'stage-bracket__check-pill--valid': isValid(participant.sourceRound) }"
                            >
                              <span class="check-wrapper">
                                <i class="fas fa-check"></i>
                                <i class="fas fa-check"></i>
                              </span>
                            </span>

                            <span
                              v-if="slotStatusKey(participant.sourceRound)"
                              :class="[
                                'stage-bracket__status-pill',
                                `stage-bracket__status-pill--${slotStatusKey(participant.sourceRound)}`
                              ]"
                            >
                              {{ statusLabel(participant.sourceRound) }}
                            </span>
                          </span>

                          <strong class="stage-bracket__slot-score">{{ participantScore(participant) }}</strong>
                        </button>
                      </div>
                    </article>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </template>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { isInnerAthlete, isOwnerJudgeFromCompetition, isOwnerOrganizer } from "@/helpers/roleUtils";

export default {
  name: "CompetitionBracket",
  props: {
    matches: {
      type: Array,
      required: true
    },
    roundSizes: {
      type: Array,
      required: true
    },
    selectedStage: {
      type: [String, Number, null],
      required: true
    },
    selectedRound: {
      type: [String, Number, null],
      required: true
    }
  },
  data() {
    return {
      defaultAvatar: require("@/assets/user.png")
    };
  },
  computed: {
    ...mapState('user', ['user']),
    ...mapState("competition", ["competitionData"]),
    stageSize() {
      const stage = (this.roundSizes || []).find((item) => Number(item?.id) === Number(this.selectedStage));
      return Number(stage?.athlete_count || 0);
    },
    normalizedMatches() {
      return (this.matches || [])
        .filter((match) => match && match.participants.some((participant) => !participant.isPlaceholder))
        .sort((left, right) => {
          const leftDisplayOrder = Number(left.displayOrder);
          const rightDisplayOrder = Number(right.displayOrder);

          if (Number.isFinite(leftDisplayOrder) && Number.isFinite(rightDisplayOrder) && leftDisplayOrder !== rightDisplayOrder) {
            return leftDisplayOrder - rightDisplayOrder;
          }

          const leftPriority = this.matchGroupPriority(left.group);
          const rightPriority = this.matchGroupPriority(right.group);

          if (leftPriority !== rightPriority) {
            return leftPriority - rightPriority;
          }

          const leftNumber = Number.isFinite(left.matchNumber) ? left.matchNumber : Number.MAX_SAFE_INTEGER;
          const rightNumber = Number.isFinite(right.matchNumber) ? right.matchNumber : Number.MAX_SAFE_INTEGER;

          if (leftNumber !== rightNumber) {
            return leftNumber - rightNumber;
          }

          return String(left.key).localeCompare(String(right.key));
        });
    },
    bracketBlocks() {
      if (!this.normalizedMatches.length) {
        return [];
      }

      const hasThirdPlaceMatch = this.normalizedMatches.some((match) => match.group === '3P');
      const isFinalStage = this.stageSize <= 2 || hasThirdPlaceMatch;

      if (!isFinalStage) {
        return [this.buildBracketBlock('main', '', this.normalizedMatches)];
      }

      return [
        this.buildBracketBlock('final', this.groupTitle('F'), this.normalizedMatches.filter((match) => match.group === 'F')),
        this.buildBracketBlock('third_place', this.groupTitle('3P'), this.normalizedMatches.filter((match) => match.group === '3P'))
      ].filter((block) => block.allMatches.length);
    }
  },
  methods: {
    isOwnerOrganizer,
    isInnerAthlete,
    isOwnerJudgeFromCompetition,
    buildConnectorGroups(matches, side) {
      const groups = [];

      for (let index = 0; index < matches.length; index += 2) {
        groups.push({
          key: `${side}-${index}`,
          matches: matches.slice(index, index + 2)
        });
      }

      return groups;
    },
    buildBracketBlock(key, label, matches) {
      const allMatches = [...matches];
      const sideMap = this.buildMatchSideMap(allMatches);
      const leftMatches = allMatches.filter((match, index) => this.resolveMatchSide(match, index, allMatches.length, sideMap) === 'left');
      const rightMatches = allMatches.filter((match, index) => this.resolveMatchSide(match, index, allMatches.length, sideMap) === 'right');
      const layout = allMatches.length <= 1 || !leftMatches.length || !rightMatches.length ? 'single' : 'paired';

      return {
        key,
        label,
        layout,
        allMatches,
        leftMatches,
        rightMatches
      };
    },
    buildMatchSideMap(matches) {
      const orderedGroups = [];

      (matches || []).forEach((match) => {
        const normalizedGroup = String(match?.group || '').trim();
        if (normalizedGroup && !orderedGroups.includes(normalizedGroup)) {
          orderedGroups.push(normalizedGroup);
        }
      });

      if (orderedGroups.length <= 1) {
        return null;
      }

      const boundary = Math.ceil(orderedGroups.length / 2);
      return new Map(
        orderedGroups.map((group, index) => [group, index < boundary ? 'left' : 'right'])
      );
    },
    resolveMatchSide(match, index, totalMatches, sideMap = null) {
      const explicitSide = String(match?.side || '').trim().toLowerCase();
      if (explicitSide === 'left' || explicitSide === 'right' || explicitSide === 'center') {
        return explicitSide === 'center'
          ? (index < Math.ceil(totalMatches / 2) ? 'left' : 'right')
          : explicitSide;
      }

      const normalizedGroup = String(match?.group || '').trim().toLowerCase();

      if (normalizedGroup === 'left') {
        return 'left';
      }

      if (normalizedGroup === 'right') {
        return 'right';
      }

      const originalGroup = String(match?.group || '').trim();
      if (sideMap && originalGroup && sideMap.has(originalGroup)) {
        return sideMap.get(originalGroup);
      }

      if (normalizedGroup) {
        const firstChar = normalizedGroup.charCodeAt(0);
        if (firstChar >= 97 && firstChar <= 122) {
          return (firstChar - 97) % 2 === 0 ? 'left' : 'right';
        }

        const numericGroup = Number.parseInt(normalizedGroup, 10);
        if (Number.isFinite(numericGroup)) {
          return numericGroup % 2 === 1 ? 'left' : 'right';
        }
      }

      return index < Math.ceil(totalMatches / 2) ? 'left' : 'right';
    },
    athleteName(athlete) {
      if (!athlete) {
        return this.$t('rounds.placeholder_player');
      }

      return [athlete.name, athlete.surname].filter(Boolean).join(' ');
    },
    groupTitle(groupKey) {
      if (groupKey === 'F') {
        return this.$t('rounds.groups.final.first_second');
      }

      if (groupKey === '3P') {
        return this.$t('rounds.groups.final.third_fourth');
      }

      return this.$t('rounds.groups.group', { group: groupKey });
    },
    matchGroupPriority(groupKey) {
      if (groupKey === 'F') return 0;
      if (groupKey === '3P') return 1;

      const normalized = String(groupKey || '').trim().toUpperCase();
      if (!normalized) return 99;

      const charCode = normalized.charCodeAt(0);
      if (charCode >= 65 && charCode <= 90) {
        return charCode - 65 + 10;
      }

      const numericGroup = Number.parseInt(normalized, 10);
      if (Number.isFinite(numericGroup)) {
        return numericGroup + 10;
      }

      return 99;
    },
    resolveSpecialStatusKey(subject) {
      if (subject?.sourceRound) {
        return this.resolveSpecialStatusKey(subject.sourceRound);
      }

      if (subject?.disqualified_at) {
        return 'disqualified';
      }

      if (subject?.injured_at) {
        return 'injured';
      }

      const numericStatusId = Number(subject?.status_id ?? subject);
      if (numericStatusId === 11) {
        return 'disqualified';
      }

      if (numericStatusId === 12) {
        return 'injured';
      }

      return '';
    },
    resolveSpecialStatusIcon(subject) {
      const statusKey = this.resolveSpecialStatusKey(subject);

      if (statusKey === 'disqualified') {
        return 'fa-solid fa-ban';
      }

      if (statusKey === 'injured') {
        return 'fa-solid fa-user-injured';
      }

      return '';
    },
    participantTitle(participant) {
      const status = this.statusLabel(participant.sourceRound);
      return status ? `${participant.name} · ${status}` : participant.name;
    },
    participantScore(participant) {
      if (participant.isPlaceholder || !this.canViewScore(participant.sourceRound)) {
        return '—';
      }

      return this.formatScore(participant.rawScore);
    },
    canViewScore(athlete) {
      if (!athlete) return false;

      const isSelf = this.isInnerAthlete(this.user, athlete);
      const isPublished = Number(athlete.status_id) === 10;

      if (isSelf && !isPublished) return false;

      return isPublished
        || this.isOwnerOrganizer(this.user, this.competitionData)
        || this.isOwnerJudgeFromCompetition(this.user, this.competitionData);
    },
    hasCompletedScoring(athlete) {
      return Boolean(athlete?.completed_scoring_at || athlete?.completedScoringAt);
    },
    isClickable(athlete) {
      if (!athlete) {
        return false;
      }

      const statusId = Number(athlete?.status_id);
      if (statusId === 11 || statusId === 12) {
        return this.hasCompletedScoring(athlete);
      }

      return [7, 10].includes(statusId);
    },
    handleAthleteClick(participant) {
      const athlete = participant?.sourceRound || null;

      if (!this.isClickable(athlete)) {
        return;
      }

      this.$emit('view-round', {
        athlete,
        selectedStage: this.selectedStage,
        selectedRound: this.selectedRound
      });
    },
    slotStatusKey(athlete) {
      if (!athlete) return null;

      const statusId = Number(athlete.status_id);
      if (statusId === 16) return 'recording';
      if (statusId === 17) return 'recorded';
      if (statusId === 10) return 'published';
      if (statusId === 13) return 'pending';
      if (statusId === 6) return 'scoring';
      if (statusId === 20) return 'uploading';
      if (statusId === 21) return 'uploaded';
      if (statusId === 12) return 'injured';
      if (statusId === 11) return 'disqualified';
      if (statusId === 7) return 'completed';
      if ([19, 22, 23].includes(statusId)) return 'review';
      return null;
    },
    statusLabel(athlete) {
      const key = this.slotStatusKey(athlete);
      const labelMap = {
        pending: this.$t('judge.pending'),
        scoring: this.$t('judge.scoring'),
        disqualified: this.$t('judge.disqualified'),
        injured: this.$t('judge.injured'),
        completed: this.$t('judge.completed'),
        recording: this.$t('judge.recording'),
        recorded: this.$t('judge.recorded'),
        published: this.$t('judge.published'),
        review: this.$t('judge.review'),
        uploading: this.$t('judge.status_20') || 'Estado 20',
        uploaded: this.$t('judge.status_21') || 'Estado 21'
      };

      return key ? labelMap[key] || key : '';
    },
    slotClasses(participant) {
      const resolvedStatusClass = this.resolveSpecialStatusKey(participant);

      return [
        {
          'stage-bracket__slot--winner': participant?.isWinner && !resolvedStatusClass,
          'stage-bracket__slot--clickable': this.isClickable(participant?.sourceRound),
          'stage-bracket__slot--empty': participant?.isPlaceholder
        },
        resolvedStatusClass ? `stage-bracket__slot--${resolvedStatusClass}` : ''
      ];
    },
    formatScore(score) {
      const value = parseFloat(score);
      return Number.isNaN(value) ? '0.00' : value.toFixed(2);
    },
    needsAlert(athlete) {
      return Number(athlete?.status_id) === 7 && this.isInnerAthlete(this.user, athlete) && !athlete.validated_at;
    },
    isOwner(athlete) {
      return Number(athlete?.status_id) === 7 && (this.isOwnerOrganizer(this.user, this.competitionData) || this.isInnerAthlete(this.user, athlete));
    },
    isValid(athlete) {
      return athlete?.validated_at;
    }
  }
};
</script>

<style scoped>
.stage-bracket {
  width: 100%;
}

.stage-bracket__stack {
  display: grid;
  gap: 1rem;
}

.stage-bracket__stack--split {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
}

.stage-bracket__layout {
  display: grid;
  gap: 1rem;
}

.stage-bracket__layout--paired {
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}

.stage-bracket__side {
  display: flex;
}

.stage-bracket__side--left {
  justify-content: flex-end;
}

.stage-bracket__side--right {
  justify-content: flex-start;
}

.stage-bracket__center {
  display: flex;
  justify-content: center;
}

.stage-bracket__column {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  min-width: 0;
  flex: 1 1 0;
}

.stage-bracket__column--center {
  width: 100%;
}

.stage-bracket__column-header {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.7rem 0.85rem;
  border-radius: 0.95rem;
  background: #eff6ff;
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.12);
  color: #0f172a;
  font-weight: 800;
  text-align: center;
}

.stage-bracket__column-header--center {
  background: #ecfeff;
  box-shadow: inset 0 0 0 1px rgba(20, 184, 166, 0.15);
}

.stage-bracket__matches {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  flex: 1 1 auto;
}

.stage-bracket__matches--center {
  justify-content: center;
}

.stage-bracket__match-groups {
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.stage-bracket__match-group {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1.6rem;
  align-items: stretch;
  gap: 0.35rem;
}

.stage-bracket__match-group--right {
  grid-template-columns: 1.6rem minmax(0, 1fr);
}

.stage-bracket__match-group-body {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.stage-bracket__match {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  padding: 0.85rem;
  border-radius: 1.1rem;
  background: linear-gradient(180deg, #dbeafe, #bfdbfe);
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.18), 0 14px 28px rgba(37, 99, 235, 0.14);
}

.stage-bracket__slots {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.stage-bracket__slot {
  border: 0;
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.7rem;
  border-radius: 999px;
  background: linear-gradient(180deg, #ffffff, #f8fafc);
  color: #0f172a;
  text-align: left;
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.18), 0 8px 18px rgba(15, 23, 42, 0.05);
  transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

.stage-bracket__slot--clickable:hover {
  transform: translateY(-1px);
  box-shadow: inset 0 0 0 1px rgba(59, 130, 246, 0.2), 0 12px 24px rgba(15, 23, 42, 0.08);
}

.stage-bracket__slot:disabled {
  cursor: default;
}

.stage-bracket__slot--winner {
  background: linear-gradient(90deg, rgba(34, 197, 94, 0.22), rgba(255, 255, 255, 0.98));
  color: #14532d;
  box-shadow: inset 0 0 0 1px rgba(22, 163, 74, 0.26), 0 10px 22px rgba(34, 197, 94, 0.16);
}

.stage-bracket__slot--winner.stage-bracket__slot--clickable:hover {
  background: linear-gradient(90deg, rgba(34, 197, 94, 0.28), rgba(255, 255, 255, 0.98));
  box-shadow: inset 0 0 0 1px rgba(21, 128, 61, 0.3), 0 14px 26px rgba(34, 197, 94, 0.2);
}

.stage-bracket__avatar-wrap {
  position: relative;
  width: 2.55rem;
  height: 2.55rem;
  flex: 0 0 2.55rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.stage-bracket__slot--winner .stage-bracket__avatar {
  border-color: #22c55e;
  box-shadow: 0 0 0 2px rgba(34, 197, 94, 0.18), 0 0 14px rgba(34, 197, 94, 0.14);
}

.stage-bracket__slot--winner .stage-bracket__slot-name,
.stage-bracket__slot--winner .stage-bracket__slot-score {
  color: #166534;
}

.stage-bracket__slot--winner .stage-bracket__slot-score {
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.55);
}

.stage-bracket__winner-mark {
  position: absolute;
  right: -0.12rem;
  bottom: -0.12rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(21, 128, 61, 0.92);
  color: #f0fdf4;
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.92), 0 8px 18px rgba(22, 163, 74, 0.35);
  font-size: 0.58rem;
  z-index: 2;
}

.stage-bracket__slot--disqualified {
  background: linear-gradient(90deg, rgba(220, 38, 38, 0.16), rgba(255, 255, 255, 0.98));
  box-shadow: inset 0 0 0 1px rgba(220, 38, 38, 0.24), 0 8px 18px rgba(15, 23, 42, 0.05);
}

.stage-bracket__slot--injured {
  background: linear-gradient(90deg, rgba(234, 88, 12, 0.16), rgba(255, 255, 255, 0.98));
  box-shadow: inset 0 0 0 1px rgba(234, 88, 12, 0.24), 0 8px 18px rgba(15, 23, 42, 0.05);
}

.stage-bracket__slot--empty {
  opacity: 0.68;
}

.stage-bracket__avatar {
  width: 100%;
  height: 100%;
  border-radius: 999px;
  object-fit: cover;
  border: 2px solid #e2e8f0;
}

.stage-bracket__slot-copy {
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.35rem;
}

.stage-bracket__slot-name {
  min-width: 0;
  flex: 1 1 8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.82rem;
  font-weight: 700;
}

.stage-bracket__slot-score {
  margin-left: auto;
  min-width: 3rem;
  text-align: right;
  font-size: 0.82rem;
  color: #0f172a;
  font-weight: 800;
}

.stage-bracket__slot-state {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.45rem;
  height: 1.45rem;
  padding: 0 0.38rem;
  border-radius: 999px;
  font-size: 0.68rem;
  flex: 0 0 auto;
}

.stage-bracket__slot-state--overlay {
  position: absolute;
  inset: 0;
  min-width: 0;
  height: auto;
  padding: 0;
  border-radius: 999px;
  font-size: 0.92rem;
  z-index: 1;
  backdrop-filter: blur(0.5px);
}

.stage-bracket__slot-state--disqualified {
  background: rgba(220, 38, 38, 0.16);
  color: #b91c1c;
}

.stage-bracket__slot-state--injured {
  background: rgba(234, 88, 12, 0.16);
  color: #c2410c;
}

.stage-bracket__slot-state--overlay.stage-bracket__slot-state--disqualified {
  background: rgba(127, 29, 29, 0.28);
  color: rgba(255, 255, 255, 0.92);
  box-shadow: inset 0 0 0 1px rgba(254, 202, 202, 0.2);
}

.stage-bracket__slot-state--overlay.stage-bracket__slot-state--injured {
  background: rgba(154, 52, 18, 0.24);
  color: rgba(255, 247, 237, 0.94);
  box-shadow: inset 0 0 0 1px rgba(254, 215, 170, 0.22);
}

.stage-bracket__status-pill,
.stage-bracket__alert-pill,
.stage-bracket__check-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 1.2rem;
  padding: 0.1rem 0.42rem;
  border-radius: 999px;
  font-size: 0.58rem;
  font-weight: 800;
  white-space: nowrap;
}

.stage-bracket__status-pill--pending { background: #e5e7eb; color: #374151; }
.stage-bracket__status-pill--scoring { background: #dbeafe; color: #1d4ed8; }
.stage-bracket__status-pill--disqualified { background: #fee2e2; color: #b91c1c; }
.stage-bracket__status-pill--injured { background: #ffedd5; color: #c2410c; }
.stage-bracket__status-pill--completed { background: #dcfce7; color: #15803d; }
.stage-bracket__status-pill--recording { background: #ffe4e6; color: #be123c; }
.stage-bracket__status-pill--recorded { background: #ecfccb; color: #4d7c0f; }
.stage-bracket__status-pill--published { background: #ede9fe; color: #6d28d9; }
.stage-bracket__status-pill--review { background: #dbeafe; color: #1e40af; }
.stage-bracket__status-pill--uploading { background: #e0f2fe; color: #0369a1; }
.stage-bracket__status-pill--uploaded { background: #ccfbf1; color: #0f766e; }

.stage-bracket__alert-pill {
  background: #fef3c7;
  color: #92400e;
  max-width: 6.4rem;
  overflow: hidden;
  justify-content: flex-start;
}

.stage-bracket__alert-pill-track {
  display: inline-flex;
  width: 100%;
  overflow: hidden;
}

.stage-bracket__alert-pill-text {
  display: inline-block;
  padding-right: 0.8rem;
  white-space: nowrap;
  animation: stage-bracket-alert-pill-marquee 13.5s linear infinite;
}

.stage-bracket__check-pill {
  background: #e5e7eb;
  color: #6b7280;
  flex: 0 0 auto;
}

.stage-bracket__check-pill--valid {
  background: #dbeafe;
  color: #2563eb;
}

.check-wrapper {
  display: inline-flex;
  align-items: center;
  flex-wrap: nowrap;
  line-height: 1;
}

.check-wrapper i + i {
  margin-left: -5px;
}

@keyframes stage-bracket-alert-pill-marquee {
  0%, 12% {
    transform: translateX(0);
  }

  88%, 100% {
    transform: translateX(calc(-100% + 5.1rem));
  }
}

.stage-bracket__connector {
  position: relative;
  display: flex;
  align-items: stretch;
  justify-content: center;
}

.stage-bracket__connector-segment {
  position: relative;
  flex: 1 1 auto;
  width: 100%;
}

.stage-bracket__connector-line,
.stage-bracket__connector-spine {
  position: absolute;
  background: rgba(148, 163, 184, 0.62);
}

.stage-bracket__connector-segment--branch .stage-bracket__connector-line--top,
.stage-bracket__connector-segment--branch .stage-bracket__connector-line--bottom {
  width: 0.8rem;
  height: 2px;
}

.stage-bracket__connector-segment--branch .stage-bracket__connector-line--top {
  top: 25%;
}

.stage-bracket__connector-segment--branch .stage-bracket__connector-line--bottom {
  top: 75%;
}

.stage-bracket__connector-segment--branch .stage-bracket__connector-spine {
  top: 25%;
  bottom: 25%;
  width: 2px;
}

.stage-bracket__connector-segment--branch .stage-bracket__connector-line--out {
  top: 50%;
  width: 0.8rem;
  height: 2px;
}

.stage-bracket__connector-segment--single .stage-bracket__connector-line--top,
.stage-bracket__connector-segment--single .stage-bracket__connector-line--bottom,
.stage-bracket__connector-segment--single .stage-bracket__connector-spine {
  display: none;
}

.stage-bracket__connector-segment--single .stage-bracket__connector-line--out {
  top: 50%;
  width: 100%;
  height: 2px;
}

.stage-bracket__connector--left .stage-bracket__connector-line--top,
.stage-bracket__connector--left .stage-bracket__connector-line--bottom,
.stage-bracket__connector--left .stage-bracket__connector-spine {
  right: 0.8rem;
}

.stage-bracket__connector--left .stage-bracket__connector-line--out {
  left: 0.8rem;
}

.stage-bracket__connector--right .stage-bracket__connector-line--top,
.stage-bracket__connector--right .stage-bracket__connector-line--bottom,
.stage-bracket__connector--right .stage-bracket__connector-spine {
  left: 0.8rem;
}

.stage-bracket__connector--right .stage-bracket__connector-line--out {
  right: 0.8rem;
}

@media (max-width: 991px) {
  .stage-bracket__stack--split,
  .stage-bracket__layout--paired {
    grid-template-columns: 1fr;
  }

  .stage-bracket__match-group,
  .stage-bracket__match-group--right {
    grid-template-columns: 1fr;
  }

  .stage-bracket__connector {
    display: none;
  }
}

@media (max-width: 576px) {
  .stage-bracket__slot {
    grid-template-columns: auto minmax(0, 1fr) auto;
    gap: 0.45rem;
    padding: 0.45rem 0.6rem;
  }

  .stage-bracket__avatar-wrap {
    width: 2.2rem;
    height: 2.2rem;
    flex-basis: 2.2rem;
  }

  .stage-bracket__slot-score {
    grid-column: auto;
    margin-left: auto;
    text-align: right;
    min-width: 2.6rem;
    font-size: 0.76rem;
  }

  .stage-bracket__slot-name {
    white-space: nowrap;
    font-size: 0.76rem;
  }

  .stage-bracket__slot-copy {
    flex-wrap: nowrap;
    align-items: center;
    overflow: hidden;
    gap: 0.28rem;
  }

  .stage-bracket__status-pill,
  .stage-bracket__alert-pill,
  .stage-bracket__check-pill {
    min-height: 1.05rem;
    padding: 0.08rem 0.34rem;
    font-size: 0.52rem;
    flex: 0 0 auto;
  }

  .stage-bracket__alert-pill {
    max-width: 5.4rem;
  }
}
</style>
